import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError, verifyDispatcherSession } from "../api/client";
import { useBootstrap } from "../components/BootstrapProvider";
import { useCart } from "../components/CartProvider";
import { EmptyState } from "../components/StateScreen";
import {
  readDispatcherHash,
  readDispatcherSession,
  writeDispatcherCall,
  writeDispatcherSession,
} from "./dispatcher-session";

/** Expirarea tokenului `dp1` (corpul e JSON in base64url, semnatura o verifica serverul). */
function tokenExpiry(token: string): string | null {
  try {
    const body = token.split(".")[1] ?? "";
    const json = JSON.parse(atob(body.replace(/-/g, "+").replace(/_/g, "/"))) as { exp?: unknown };
    return typeof json.exp === "number" ? new Date(json.exp).toISOString() : null;
  } catch {
    return null;
  }
}

/**
 * `/dispecer#t=<sesiune>&call=<telefon>&name=<nume>` — linkul deschis din POS /
 * Symbai Staff la „Notează pe site-ul pieței". Verifica sesiunea la core, o tine
 * in fila curenta, porneste apelul (cos gol, client nou) si duce la site.
 */
export function DispatcherEntry() {
  const { marketKey } = useBootstrap();
  const { resetSession } = useCart();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const { token, phone, name } = readDispatcherHash(window.location.hash);
    // Fragmentul (si nonce-ul `n`) nu raman in istoric: un refresh nu reporneste
    // apelul. `?market=` (dezvoltare) ramane, altfel piata nu se mai rezolva.
    const query = new URLSearchParams(window.location.search);
    query.delete("n");
    const rest = query.toString();
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${rest ? `?${rest}` : ""}`);
    const startCall = () => {
      resetSession();
      writeDispatcherCall(marketKey, phone === null ? null : { phone, name });
      navigate("/", { replace: true });
    };
    if (token === null) {
      if (readDispatcherSession(marketKey) !== null) {
        startCall();
        return;
      }
      setError("Linkul nu are sesiunea dispecerului. Deschide modul dispecer din POS sau din Symbai Staff.");
      return;
    }
    let cancelled = false;
    verifyDispatcherSession(marketKey, token)
      .then((info) => {
        if (cancelled) return;
        writeDispatcherSession(marketKey, {
          token,
          operator: info.operator,
          marketName: info.marketName,
          expiresAt: tokenExpiry(token),
          rejected: false,
        });
        startCall();
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(caught instanceof ApiError ? caught.message : "Sesiunea dispecerului nu a putut fi verificată.");
      });
    return () => {
      cancelled = true;
    };
    // Rulează o singură dată, la deschiderea linkului.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error !== null) {
    return <EmptyState title="Modul dispecer nu a pornit" description={error} />;
  }
  return <EmptyState title="Pornesc modul dispecer…" description="Verific sesiunea și pregătesc comanda clientului." />;
}
